import { useEffect, useRef } from 'react'

export const COMMAND_PALETTE_OPEN_EVENT = 'xingroute:command-palette-open'

export function openCommandPalette(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(COMMAND_PALETTE_OPEN_EVENT))
}

export type StylePresetShortcut = 1 | 2 | 3

interface DetailShortcutHandlers {
  enabled: boolean
  onToggleStage?: () => void
  onStylePreset?: (preset: StylePresetShortcut) => void
  onPreviousWaypoint?: () => void
  onNextWaypoint?: () => void
  onEdit?: () => void
  onCancelEdit?: () => void
  onShowCheatsheet?: () => void
  onFocusTitleSearch?: () => void
}

interface ShortcutOptions {
  enabled?: boolean
  onToggleCommandPalette?: () => void
  detail?: DetailShortcutHandlers
}

export function useShortcuts(options: ShortcutOptions): void {
  const optionsRef = useRef(options)
  optionsRef.current = options

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const opts = optionsRef.current
      if (opts.enabled === false) return

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        opts.onToggleCommandPalette?.()
        return
      }

      const detail = opts.detail
      if (!detail?.enabled) return
      if (isInsideCommandPalette(event.target)) return

      const interactive = isInteractiveTarget(event.target)
      if (event.key === 'Escape') {
        if (!detail.onCancelEdit) return
        event.preventDefault()
        detail.onCancelEdit()
        return
      }

      if (interactive || event.metaKey || event.ctrlKey || event.altKey) return

      if (event.key === 'm') {
        if (!detail.onToggleStage) return
        event.preventDefault()
        detail.onToggleStage()
        return
      }

      if (event.key === '[') {
        if (!detail.onPreviousWaypoint) return
        event.preventDefault()
        detail.onPreviousWaypoint()
        return
      }

      if (event.key === ']') {
        if (!detail.onNextWaypoint) return
        event.preventDefault()
        detail.onNextWaypoint()
        return
      }

      if (event.key === 'e') {
        if (!detail.onEdit) return
        event.preventDefault()
        detail.onEdit()
        return
      }

      if (event.key === '?') {
        if (!detail.onShowCheatsheet) return
        event.preventDefault()
        detail.onShowCheatsheet()
        return
      }

      if (event.key === '/') {
        event.preventDefault()
        const focusTitleSearch = detail.onFocusTitleSearch ?? openCommandPalette
        focusTitleSearch()
        return
      }

      if (event.key === '1' || event.key === '2' || event.key === '3') {
        if (!detail.onStylePreset) return
        event.preventDefault()
        detail.onStylePreset(Number(event.key) as StylePresetShortcut)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
}

function isInsideCommandPalette(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('[data-command-palette]'))
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  if (target.closest('[contenteditable="true"]')) return true
  const tagName = target.tagName.toLowerCase()
  return tagName === 'input' || tagName === 'textarea' || tagName === 'select'
}
